import {type VesselTag} from '../api/vessels/vessel-tags.ts'
import {type Location, type LocationIdType} from '../store/state/locations/state'
import {type GuidType} from '../values/generic-type-definitions'
import {TimeRange, type TimeUnit} from '../values/TimeRange'
import {SavedFilterPageType} from '../store/state/saved-filters/state.ts'
import type {CyberOwlTheme} from '../theme/theme.ts'
import {NodeValues} from '../values/nodes/NodeData.ts'

export class StringUtils {
    public static readonly Separators = {
        COMMA: ',',
        SEMICOLON: ';',
        TAB: '\t',
        PIPE: '|',
        WHITESPACE: /\s+/,
        NEWLINE: /\r\n|\n|\r/,
    }
    /**
     * Ensures value(s) strings and has a size after trim
     * @returns {boolean}
     * @param stringToCheck
     */
    public static validString(stringToCheck: unknown): stringToCheck is string {
        if (typeof stringToCheck !== 'string') {
            return false
        }
        return !(stringToCheck.length === 0 || stringToCheck.trim().length === 0)
    }

    public static capitaliseString(string: string): string {
        return `${string.charAt(0).toUpperCase()}${string.slice(1)}`
    }

    public static titleCaseString(string: string): string {
        return string
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
    }

    public static chop(string: string): string {
        return string.substring(0, string.length - 1)
    }

    public static isBetweenMinAndMaxInclusive(
        stringToCheck: string,
        min: number,
        max: number,
    ): boolean {
        return stringToCheck.length >= min && stringToCheck.length <= max
    }

    public static separateStringIntoItems(text: string, customSeparator?: string | RegExp) {
        const hasTabCharacters = text.includes('\t')
        const hasMultipleLines = /[\r\n]/.test(text)
        const isFromSpreadsheet = hasMultipleLines

        let items: string[] = []

        if (isFromSpreadsheet) {
            const rows = text.split(/[\r\n]+/).filter((row) => row.trim())

            items = rows.flatMap((row) => {
                if (hasTabCharacters) {
                    return row
                        .split('\t')
                        .map((cell) => cell.trim())
                        .filter(Boolean)
                }
                const value = row.trim()
                return value ? [value] : []
            })
        }
        // Custom separator
        else {
            items = text
                .trim()
                .split(customSeparator ?? this.Separators.WHITESPACE)
                .map((value) => value.trim())
                .filter(Boolean)
        }

        return {
            items,
            isFromSpreadsheet,
            originalText: text,
        }
    }
}

export class BrowserUtils {
    public static isBrowserDarkMode(): boolean {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    }
}

export class TagsUtils {
    public static getVesselCountForTag(tag: VesselTag, locations: Location[]): number {
        return locations.filter((l) => l.tags?.some((t) => t.identity === tag.identity)).length
    }
    public static getVesselCountWithTagName(tagName: string, locations: Location[]): number {
        return locations.filter((l) => l.tags?.some((t) => t.name === tagName)).length
    }
}

export function getTagName(
    existingTags: VesselTag[],
    tagId: GuidType | undefined,
): string | undefined {
    return existingTags.find((tags) => tags.identity === tagId)?.name
}

export function populateLocations(
    locations: string | LocationIdType[] | undefined,
): Set<LocationIdType> | undefined {
    if (!locations || locations.length === 0 || locations === 'all-selected') {
        return undefined
    }
    if (locations === 'all-deselected') {
        return new Set<LocationIdType>()
    }
    return new Set<LocationIdType>(locations)
}

export function populateLocationsForFilterCriteria(
    locations: Set<LocationIdType> | undefined,
): LocationIdType[] | undefined {
    if (!locations) {
        return undefined
    }
    return Array.from(locations)
}

export function formattedLocationsBlockSet(locations: unknown): string[] | undefined {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return !Array.isArray(locations) ? undefined : (locations ?? undefined)
}

const validEmailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
export function isEmailValid(email: string | undefined | null): boolean {
    if (!StringUtils.validString(email)) {
        return true
    }
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return email !== undefined && validEmailRegex.test(email)
}

export function getRelativeTimeRange(timeRange: TimeRange | null): string | null {
    return timeRange
        ? new TimeRange(timeRange.value, timeRange.unit, timeRange.name).toRelativeString()
        : null
}

export function getTimeRangeCriteria(relativeTimeRange: string | null): TimeRange | null {
    if (relativeTimeRange == null) {
        return null
    }

    const unit = relativeTimeRange.slice(-1) as TimeUnit
    const value = Number(StringUtils.chop(relativeTimeRange.substring(8)))
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const name = `${value} ${unit === 'h' ? 'Hours' : 'Days'}`

    return new TimeRange(value, unit, name)
}

export function getReportType(reportType: string | undefined): string {
    if (reportType == undefined) {
        return 'Unknown'
    }

    return reportType === '' ? 'All' : reportType
}

export function setActive(t: SavedFilterPageType): void {
    // incidents and incidents reports are the same group, but not changed via a dropdown from the nav
    // because of this, the incidents-report is totally disconnected a from the nav as it's technically not a real nav item
    // this will force it to be one until we remove the `view level` and move it to a dropdown like the rest
    if (t === SavedFilterPageType.INCIDENTS || t === SavedFilterPageType.INCIDENTS_REPORT) {
        document.getElementById('navigation-incidents')?.classList.add('active', 'activeLink')
    }
}

export class TypedObject {
    public static entries<T extends object>(obj: T) {
        type K = keyof T
        type Entry = {[P in K]: [P, T[P]]}[K] // Maps each key to its specific [key, value] tuple type
        return Object.entries(obj) as Array<Entry>
    }

    public static keys<T extends object>(obj: T) {
        type K = keyof T
        return Object.keys(obj) as Array<K>
    }
}

export function getNodeValueBackgroundColor(
    theme: CyberOwlTheme,
    value: NodeValues | undefined,
): string {
    switch (value) {
        case NodeValues.HIGH:
            return theme.threatScores.score.lowScoreHighValue.background
        case NodeValues.MEDIUM:
            return theme.threatScores.score.lowScoreMediumValue.background
        case NodeValues.LOW:
        default:
            return theme.threatScores.score.lowScoreLowValue.background
    }
}
